import React, { useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Body2, InfoBox, SkeletonLoader, Link, Card } from "@activecollab/components";
import ExternalEmptySlate from "~/components/EmptySlate/ExternalEmptySlate";
import { useAuth } from "~/components/Hooks/useAuth";
import { PublicInvoiceObjectItemsTable } from "~/components/InvoiceObject/PublicInvoiceObjectItemsTable";
import PublicInvoiceObjectViewHeader from "~/components/InvoiceObject/PublicInvoiceObjectViewHeader";
import PublicInvoiceStatus from "~/components/InvoiceObject/PublicInvoiceStatus";
import PublicLayout from "~/pages/Layouts/PublicLayout";
import { usePublicInvoice } from "~/repository";
import localizationService from "~/services/LocalizationService";
import Angie from "~/utils/angie.jsx";

export const PublicInvoicePage = () => {
  useAuth();
  const [searchParams] = useSearchParams();
  const { data, isLoading, isFetched, isError } = usePublicInvoice(
    searchParams.get("number"),
    searchParams.get("hash"),
    { enabled: !!searchParams.get("number") && !!searchParams.get("hash") }
  );
  const { t } = useTranslation();
  const shouldShowEInvoice = useMemo(() => {
    return !!(
      isFetched &&
      data?.e_invoices_for_clients_enabled &&
      data?.invoice?.company_address_line_1 &&
      data?.invoice?.company_city &&
      data?.invoice?.company_country &&
      data?.invoice?.company_zip_code &&
      data?.invoice?.company_tax_number
    );
  }, [
    data?.invoice?.company_address_line_1,
    data?.invoice?.company_city,
    data?.invoice?.company_country,
    data?.invoice?.company_tax_number,
    data?.invoice?.company_zip_code,
    data?.e_invoices_for_clients_enabled,
    isFetched,
  ]);

  useEffect(() => {
    localizationService.configure({ language_id: data?.language?.id }, [data?.language], true);
  }, [data?.language]);

  return (isFetched && isError) || (isFetched && !data) || !searchParams.get("number") || !searchParams.get("hash") ? (
    <PublicLayout
      title={t("Not Found")}
      shouldShowTerms={false}
    >
      <ExternalEmptySlate isLoggedIn={false} />
    </PublicLayout>
  ) : (
    <PublicLayout
      title={`Invoice ${data?.invoice?.number} - ActiveCollab`}
      shouldShowLogo
      paperClassName="tw-pt-0 tw-pb-7"
      topSection={
        <Card
          className="tw-mb-4"
          variant="paper-2"
          hoverable
        >
          <InfoBox
            type="default"
            showIcon
          >
            {isLoading ? (
              <SkeletonLoader className="tw-w-3/4" />
            ) : (
              <Trans>
                <Body2 data-testid="public-invoice-header">
                  This invoice was issued by <b>{{ creator_name: data?.creator?.display_name }}</b> (
                  <Link
                    variant="text link-primary"
                    href={`mailto:${data?.creator?.email}`}
                  >
                    {{ creator_email: data?.creator?.email }}
                  </Link>
                  ) from <b>{{ company: data?.creator_company?.name }}</b>.
                </Body2>
              </Trans>
            )}
          </InfoBox>
        </Card>
      }
    >
      <div className="tw-flex tw-flex-col tw-flex-1">
        <PublicInvoiceStatus
          data={data}
          isLoading={isLoading}
          eInvoice={shouldShowEInvoice}
        />
        <PublicInvoiceObjectViewHeader
          object={data?.invoice}
          isLoading={isLoading}
          template={data?.template}
          currency={data?.currency}
          dateFormat={Angie.functions.phpToMomentDateFormat(data?.format_date)}
        />

        <div className="tw-mx-7 tw-grow">
          {isLoading ? (
            <div className="tw-flex tw-flex-1 tw-flex-col">
              {[1, 2, 3, 4].map((item) => {
                return (
                  <div
                    className="tw-flex tw-flex-row tw-h-[41.5px] tw-border-0 tw-border-b tw-border-solid tw-border-b-gray-100 tw-items-center"
                    key={item}
                  >
                    <SkeletonLoader className="tw-w-[31px]" />
                    <div className="tw-grow">
                      <SkeletonLoader />
                    </div>
                    <SkeletonLoader className="tw-w-[63px]" />
                    <SkeletonLoader className="tw-w-[143px]" />
                    <SkeletonLoader className="tw-w-[143px]" />
                  </div>
                );
              })}

              {[1, 2, 3, 4, 5].map((item) => {
                return (
                  <div
                    className="tw-flex tw-justify-end"
                    key={item}
                  >
                    <div className="tw-flex tw-h-[41.5px] tw-items-center tw-w-[286px] tw-border-0 tw-border-b tw-border-solid tw-border-b-gray-100 tw-justify-end">
                      <SkeletonLoader className="tw-w-[143px]" />
                      <SkeletonLoader className="tw-w-[143px]" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : data?.items?.length ? (
            <PublicInvoiceObjectItemsTable
              items={data?.items}
              item={data?.invoice}
              className="tw-w-full tw-mb-14"
              currency={data?.currency}
            />
          ) : null}
        </div>
      </div>
      {data?.invoice?.note ? (
        <div className="tw-mx-7">
          <div className="separator tw-border tw-border-solid tw-border-border-primary tw-border-b-0 tw-mb-4 tw-mt-4" />
          <Body2 color="tertiary">{data?.invoice?.note}</Body2>
        </div>
      ) : null}
    </PublicLayout>
  );
};

export default PublicInvoicePage;
